import * as RadixSelect from '@radix-ui/react-select';
import clsx from 'clsx';
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { ReactComponent as DropdownSuffixIcon } from './icons/dropdown-suffix-icon.svg';
import { ReactComponent as ButtonLoader } from './icons/button-loader.svg';
import { twMerge } from 'tailwind-merge';
import useOnTapOutside from '../hooks/useOnTapOutside';

export type DropdownOption = {
  value: string;
  label: string;
};

type SelectProps = {
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
  isLoading?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
};

const Select: React.FC<SelectProps> = ({
  options,
  onChange,
  isLoading,
  value,
  placeholder = 'Choose an option',
  triggerClassName,
  contentClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const [triggerElement, setTriggerElement] = useState<HTMLButtonElement | null>(null);
  const [openDirection, setOpenDirection] = useState<string | null>(null);

  useOnTapOutside([triggerElement, contentElement], () => {
    openChangeHandler(false);
  });

  const selectedItem = useMemo(() => {
    return options.find((option) => option.value === value);
  }, [options, value]);

  const openChangeHandler = (open: boolean) => {
    setIsOpen(open);

    setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 0);
  };

  useEffect(() => {
    const handleSideChange = () => {
      const side = contentElement?.dataset['side'];
      setOpenDirection(side ?? null);
    };

    const observer = new MutationObserver(handleSideChange);

    if (contentElement) {
      observer.observe(contentElement, {
        attributes: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [contentElement]);

  return (
    <RadixSelect.Root open={isOpen} onValueChange={onChange} value={value} onOpenChange={openChangeHandler}>
      <RadixSelect.Trigger
        ref={setTriggerElement}
        aria-label={selectedItem?.label ?? placeholder}
        style={{ transitionDuration: `10ms` }}
        className={twMerge(
          clsx(
            'relative flex items-center transition-all bg-green-dark outline-none focus:outline-none hover:outline-none focus:bg-green-forest w-64 sm:w-64 h-11 px-3.5 text-green-laser border-2 border-green-laser z-40',
            {
              'rounded-t-2': isOpen && openDirection === 'bottom',
              'rounded-b-2': isOpen && openDirection === 'top',
              'rounded-2': !isOpen || openDirection === null,
            },
            triggerClassName
          )
        )}
      >
        <div
          className={clsx(
            'flex flex-1 sm:text-[1rem]/[1.25rem] md:text-[1.25rem]/[1.625rem] font-medium z-30 font-aeonik'
          )}
        >
          {selectedItem?.label ?? placeholder}
        </div>

        <RadixSelect.Icon className={clsx('absolute right-3.5')}>
          {isLoading ? (
            <ButtonLoader className="w-6 h-6 animate-infinitely-spinning" />
          ) : (
            <DropdownSuffixIcon
              className={clsx('pointer-events-none w-4 h-4 right-0', {
                'rotate-180': isOpen,
              })}
            />
          )}
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <>
          <Overlay open={isOpen} />
          <RadixSelect.Content
            ref={setContentElement}
            position="popper"
            className={twMerge(
              clsx(
                'max-h-50 pointer-events-none origin-top w-[var(--radix-select-trigger-width)]  border-green-laser border-x-2 overflow-y-scroll overflow-hidden bg-green-dark text-[1.25rem]/[1.625rem] font-medium font-aeonik z-20',
                contentClassName,
                {
                  'rounded-b-2 border-b-2': openDirection === 'bottom',
                  'rounded-t-2 border-t-2': openDirection === 'top',
                }
              )
            )}
          >
            <RadixSelect.Viewport className="box-border">
              {options.map((option, index) => (
                <SelectItem selected={value} key={option.value} value={option.value} index={index}>
                  {option.label}
                </SelectItem>
              ))}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
};

export default Select;

type SelectItemProps = {
  value: string;
  disabled?: boolean;
  className?: string;
  selected: string;
  index: number;
};

const SelectItem = React.forwardRef<HTMLDivElement, PropsWithChildren<SelectItemProps>>(
  ({ children, className, index, ...props }, forwardedRef) => {
    return (
      <RadixSelect.Item
        className={clsx(
          'w-full h-11 flex outline-none focus:outline-none hover:outline-none items-center  border-green-laser selection:bg-green-forest text-green-laser hover:bg-green-forest px-3.5 [&[data-highlighted]]:bg-green-forest cursor-pointer',
          {
            'text-white': props.value === props.selected,
            'border-t-2': index !== 0,
          },
          className
        )}
        {...props}
        ref={forwardedRef}
      >
        <RadixSelect.ItemText>{children}</RadixSelect.ItemText>
      </RadixSelect.Item>
    );
  }
);

const Overlay = ({ open }: { open: boolean }) => {
  const [visible, setVisible] = useState(open);
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setVisible(false);
      }, 100);
      return () => {
        clearTimeout(timer);
      };
    }
    setVisible(true);
  }, [open]);

  return visible ? <div className="fixed top-0 bottom-0 left-0 right-0" onClick={(e) => e.stopPropagation()} /> : null;
};
